import { useCallback, useEffect, useRef, useState } from "react";
import { EMPTY_WORD_DOC_CONTEXT, type WordDocumentContext, type HandoffPayload } from "../types";
import {
  countWords,
  enableAutoShowTaskpane,
  getWordDocumentContext,
  readWordSelection,
} from "../../../../integrations/tools/word/services/wordDocument";
import { consumeHandoff } from "../services/handoff";
import { DOCUMENT_CONTEXT_CHAR_CAP } from "../../../chat/services/api";

export interface UseWordDocumentReturn {
  doc: WordDocumentContext;
  fullDoc: WordDocumentContext;
  loading: boolean;
  /** True when the last read threw. Distinguishes "could not read the document"
   *  from the empty document both leave behind in `fullDoc`, so the greeting
   *  does not report an unreadable file as an empty one. */
  readFailed: boolean;
  handoff: HandoffPayload | null;
  hasSelection: boolean;
  /** True while what a turn would actually send — the selection if one
   *  exists, otherwise the whole document — is larger than the chat context
   *  cap. Only the first DOCUMENT_CONTEXT_CHAR_CAP characters ride the turn,
   *  and the user must learn that BEFORE asking, not from a half-answered
   *  question. Covers a big selection AND a big whole document sent with
   *  nothing selected (e.g. /write on a long document) — `doc` already holds
   *  whichever of the two will actually ride the turn, so one check serves
   *  both. State, not an event: the warning banner it drives persists exactly
   *  as long as the oversized content does. */
  selectionOverCap: boolean;
  refresh: () => Promise<void>;
  /** Re-read the document AND the live selection, returning the context a chat
   *  turn should send. The watcher above is event-driven, and Word does not
   *  reliably fire DocumentSelectionChanged on a deselect (Word on the web in
   *  particular) — a turn that trusts the cached `doc` can re-answer over a
   *  selection the user already let go of, and over body text as it stood at
   *  mount. Falls back to the cached context when the live reads fail, and
   *  keeps the focus-steal retention: an empty read inside the window after the
   *  task pane took focus means the click that started this send probably took
   *  the selection with it, not that the user deselected. */
  resolveSendContext: () => Promise<WordDocumentContext>;
}

const SELECTION_CLEAR_DELAY_MS = 2_000;
/** How recently the task pane must have taken focus for a vanished selection to
 *  be read as collateral rather than intent.
 *
 *  The two cases are indistinguishable to Office.js — `DocumentSelectionChanged`
 *  carries no payload and `readWordSelection` returns "" for both — so the
 *  signal has to come from the DOM: clicking in the document to deselect never
 *  gives the task pane focus, while clicking the input or Send always does.
 *  Sized to cover the gap between the focus event and Word reporting the
 *  clearance, not the user's thinking time. */
export const FOCUS_STEAL_WINDOW_MS = 1_000;
/** Word fires DocumentSelectionChanged repeatedly while the user drags across
 *  paragraphs. Coalesce the burst into one read of the settled selection
 *  instead of one read per event (AIVA-6925). */
const SELECTION_DEBOUNCE_MS = 200;

/** The document context narrowed to the selection: same document identity, but
 *  the selection's own text and counts.
 *
 *  `markdown` is the same selection with its heading paragraphs marked (`# `,
 *  `## `, …), empty when the selection has no headings. It becomes the context
 *  TEXT — the model cannot preserve a section's structure it was never shown
 *  (AIVA-7080) — while the counts stay measured on the plain selection, so the
 *  markers never inflate the word or character totals the greeting reports. */
function withSelection(
  base: WordDocumentContext,
  selected: string,
  markdown = "",
): WordDocumentContext {
  return {
    ...base,
    text: markdown || selected,
    wordCount: countWords(selected),
    charCount: selected.length,
    hasSelection: true,
  };
}

export function useWordDocument(enabled: boolean): UseWordDocumentReturn {
  const [doc, setDoc] = useState<WordDocumentContext>(EMPTY_WORD_DOC_CONTEXT);
  const [fullDoc, setFullDoc] = useState<WordDocumentContext>(EMPTY_WORD_DOC_CONTEXT);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [readFailed, setReadFailed] = useState(false);
  const [handoff, setHandoff] = useState<HandoffPayload | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const fullDocRef = useRef<WordDocumentContext>(EMPTY_WORD_DOC_CONTEXT);
  // Live mirror of `doc` so resolveSendContext can fall back to the held
  // context after its own awaits without closing over stale state.
  const docRef = useRef<WordDocumentContext>(EMPTY_WORD_DOC_CONTEXT);
  // Tracks the last confirmed non-empty selection so context is not lost when
  // the user clicks into the task pane input (which clears the Word selection
  // in some Office hosts before they finish typing their message).
  const selectionClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id of the newest selection read. A read that resolves after a
  // later one started describes a selection that no longer exists, and applying
  // it is the last-resolve-wins race that left transformation commands holding
  // only the first paragraph of a multi-paragraph selection (AIVA-6925).
  const selectionRunRef = useRef(0);
  // Live mirror of `hasSelection` for refresh() to consult after its own await.
  const hasSelectionRef = useRef(false);
  // When the task pane last took focus. Negative infinity, not 0, so the first
  // selection clear of a session is treated as deliberate rather than as
  // happening "at the epoch, therefore long ago" by accident of arithmetic.
  const taskPaneFocusedAtRef = useRef(Number.NEGATIVE_INFINITY);

  // Single write point for `doc` so the ref mirror can never disagree with the
  // state resolveSendContext and the renders read.
  const commitDoc = useCallback((ctx: WordDocumentContext) => {
    docRef.current = ctx;
    setDoc(ctx);
  }, []);

  const applySelectionClear = useCallback(() => {
    selectionClearTimerRef.current = null;
    const cleared = { ...fullDocRef.current, hasSelection: false };
    commitDoc(cleared);
    setFullDoc(fullDocRef.current);
  }, [commitDoc]);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const ctx = await getWordDocumentContext();
      fullDocRef.current = ctx;
      setFullDoc(ctx);
      setReadFailed(false);
      // Deliberately NOT a second selection read: asking a different API
      // whether anything is selected is how a live multi-paragraph selection
      // used to get overwritten with whole-document context (AIVA-6925). The
      // watcher below owns that fact; read it as late as possible, after the
      // await above, so a selection that landed meanwhile is respected.
      if (!hasSelectionRef.current) commitDoc(ctx);
    } catch (e) {
      setReadFailed(true);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [enabled, commitDoc]);

  useEffect(() => {
    if (!enabled) {
      commitDoc(EMPTY_WORD_DOC_CONTEXT);
      setLoading(false);
      setReadFailed(false);
      setHandoff(null);
      hasSelectionRef.current = false;
      setHasSelection(false);
      return;
    }
    setHandoff(consumeHandoff());
    enableAutoShowTaskpane().catch(() => undefined);
    refresh().catch(() => undefined);
  }, [enabled, refresh, commitDoc]);

  useEffect(() => {
    if (!enabled) return;

    // One read answers both questions — whether anything is selected and what
    // it says — so the two can never disagree about how much was selected.
    const readSelection = () => {
      const run = ++selectionRunRef.current;
      readWordSelection()
        .then(({ ok, text, markdown }) => {
          if (run !== selectionRunRef.current) return; // superseded mid-flight
          if (!ok) return; // read failed: keep what we had rather than inventing a clear
          const selected = text.trim();
          hasSelectionRef.current = selected.length > 0;
          setHasSelection(selected.length > 0);
          if (selected) {
            if (selectionClearTimerRef.current) {
              clearTimeout(selectionClearTimerRef.current);
              selectionClearTimerRef.current = null;
            }
            commitDoc(withSelection(fullDocRef.current, selected, markdown));
          } else if (Date.now() - taskPaneFocusedAtRef.current < FOCUS_STEAL_WINDOW_MS) {
            // The task pane just took focus, so the selection most likely went
            // with it rather than because the user let it go. Hold the text so
            // a command typed in the pane still has something to act on.
            if (selectionClearTimerRef.current) {
              clearTimeout(selectionClearTimerRef.current);
            }
            selectionClearTimerRef.current = setTimeout(applySelectionClear, SELECTION_CLEAR_DELAY_MS);
          } else {
            // Word had focus when the selection went: the user deselected on
            // purpose. Retaining it here is what let a selection-required slash
            // command run against a document with nothing selected — the guard
            // read a selection that was already gone (AIVA-7078).
            if (selectionClearTimerRef.current) {
              clearTimeout(selectionClearTimerRef.current);
            }
            applySelectionClear();
          }
        })
        .catch(() => undefined);
    };

    const checkSelection = () => {
      if (selectionDebounceRef.current) clearTimeout(selectionDebounceRef.current);
      selectionDebounceRef.current = setTimeout(() => {
        selectionDebounceRef.current = null;
        readSelection();
      }, SELECTION_DEBOUNCE_MS);
    };

    // Focus is the only signal that separates "the user deselected" from "the
    // task pane took the selection with it", so it is tracked on the window
    // rather than sampled at event time: the selection event and the focus
    // transfer are unordered, and sampling would race them.
    const noteTaskPaneFocus = () => {
      taskPaneFocusedAtRef.current = Date.now();
    };
    window.addEventListener("focus", noteTaskPaneFocus);
    if (typeof document !== "undefined" && document.hasFocus?.()) noteTaskPaneFocus();

    // The first read is not debounced: nothing has fired yet, and the greeting
    // should not wait on a timer.
    readSelection();

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        checkSelection,
      );
    } catch { /* not supported in this host */ }

    return () => {
      window.removeEventListener("focus", noteTaskPaneFocus);
      // Bump the run id so any read still in flight cannot land after unmount.
      selectionRunRef.current += 1;
      if (selectionDebounceRef.current) {
        clearTimeout(selectionDebounceRef.current);
        selectionDebounceRef.current = null;
      }
      if (selectionClearTimerRef.current) {
        clearTimeout(selectionClearTimerRef.current);
        selectionClearTimerRef.current = null;
      }
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: checkSelection } as Office.RemoveHandlerOptions,
        );
      } catch { /* ignore */ }
    };
  }, [enabled]);

  // Sync the watcher state to a fresh non-empty selection read — unless a
  // newer read landed meanwhile, in which case that read owns the state and
  // this one only feeds the turn that requested it.
  const commitFreshSelection = useCallback(
    (run: number, ctx: WordDocumentContext) => {
      if (run !== selectionRunRef.current) return;
      if (selectionClearTimerRef.current) {
        clearTimeout(selectionClearTimerRef.current);
        selectionClearTimerRef.current = null;
      }
      hasSelectionRef.current = true;
      setHasSelection(true);
      commitDoc(ctx);
    },
    [commitDoc],
  );

  // Sync the watcher state to a fresh confirmed deselect (same newer-read
  // guard as above).
  const commitFreshClear = useCallback(
    (run: number) => {
      if (run !== selectionRunRef.current) return;
      hasSelectionRef.current = false;
      setHasSelection(false);
      if (selectionClearTimerRef.current) {
        clearTimeout(selectionClearTimerRef.current);
      }
      applySelectionClear();
    },
    [applySelectionClear],
  );

  // Send-time context resolution. The watcher above only hears about the
  // selection when Word fires DocumentSelectionChanged, and Word does not
  // guarantee that event on a deselect — so a slash command run after the user
  // clicked away could still ride on the previous selection. Re-reading here
  // makes each turn's context a fresh fact rather than a cached one; the
  // cached state is only the fallback for a host that refuses the reads.
  const resolveSendContext = useCallback(async (): Promise<WordDocumentContext> => {
    if (!enabled) return EMPTY_WORD_DOC_CONTEXT;

    // Fresh body first, so a cleared selection falls back to the document as
    // it stands NOW — not as it stood when the pane mounted. Best-effort: a
    // failed read keeps the last good one, same as the watcher.
    try {
      const ctx = await getWordDocumentContext();
      fullDocRef.current = ctx;
      setFullDoc(ctx);
      setReadFailed(false);
    } catch { /* keep the last good document read */ }

    // Supersede any in-flight watcher read: this read is newer by definition,
    // and a slower earlier one must not overwrite what this turn sends.
    const run = ++selectionRunRef.current;
    try {
      const { ok, text, markdown } = await readWordSelection();
      // A refused read (!ok) keeps the held context rather than inventing a clear.
      const selected = ok ? text.trim() : "";
      if (selected) {
        const ctx = withSelection(fullDocRef.current, selected, markdown);
        commitFreshSelection(run, ctx);
        return ctx;
      }
      if (ok && Date.now() - taskPaneFocusedAtRef.current >= FOCUS_STEAL_WINDOW_MS) {
        // Nothing selected and the task pane did not just take focus: the
        // user let the selection go, whether or not Word ever said so.
        commitFreshClear(run);
        return { ...fullDocRef.current, hasSelection: false };
      }
      // Empty read inside the focus-steal window: the click that started
      // this send likely took the selection with it (AIVA-7078's retention
      // case) — fall through to the held context.
    } catch { /* read failed — fall through to the held context */ }
    return hasSelectionRef.current ? docRef.current : { ...fullDocRef.current, hasSelection: false };
  }, [enabled, commitFreshSelection, commitFreshClear]);

  // Derived from the same doc state a turn would send, so the banner and the
  // payload can never disagree about whether truncation will happen. No
  // hasSelection gate: `doc` already holds the selection's text when one
  // exists, and the whole document's text otherwise (see `refresh` above),
  // so this one check is accurate for both cases.
  const selectionOverCap = doc.charCount > DOCUMENT_CONTEXT_CHAR_CAP;

  return { doc, fullDoc, loading, readFailed, handoff, hasSelection, selectionOverCap, refresh, resolveSendContext };
}
